/**
 * Download Status Component - Display download system health and observability
 */
import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './DownloadStatus.css';

const DownloadStatus = ({ onClose }) => {
  const [stats, setStats] = useState(null);
  const [activeDownloads, setActiveDownloads] = useState([]);
  const [failedDownloads, setFailedDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSongLog, setSelectedSongLog] = useState(null);

  useEffect(() => {
    fetchDownloadStatus();
    const interval = setInterval(fetchDownloadStatus, 5000); // Update every 5 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchDownloadStatus = async () => {
    try {
      setError(null);
      const [statsResponse, activeResponse, failedResponse] = await Promise.all([
        api.get('/stats'),
        api.get('/downloads/active'),
        api.get('/downloads/failed')
      ]);

      setStats(statsResponse.data);
      setActiveDownloads(activeResponse.data.active_downloads || []);
      setFailedDownloads(failedResponse.data.failed_downloads || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching download status:', err);
      setError(err.response?.data?.error || 'Failed to fetch download status');
      setLoading(false);
    }
  };

  const handleViewLog = async (songId) => {
    try {
      const response = await api.get(`/songs/${songId}/download-status`);
      setSelectedSongLog(response.data);
    } catch (err) {
      console.error('Error fetching download log:', err);
    }
  };

  const getSystemHealthStatus = () => {
    if (!stats?.download_system) return 'unknown';

    const { ytdlp_available, active_downloads } = stats.download_system;

    if (!ytdlp_available) {
      return 'critical';
    } else if (active_downloads > 0) {
      return 'active';
    } else {
      return 'healthy';
    }
  };

  const getSystemHealthMessage = () => {
    const status = getSystemHealthStatus();

    switch (status) {
      case 'critical':
        return 'yt-dlp is not installed - downloads cannot complete';
      case 'active':
        return `Active downloads: ${stats.download_system.active_downloads}`;
      case 'healthy':
        return 'Download system ready';
      default:
        return 'Status unknown';
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-header">
            <h2>Download Status</h2>
            <button className="btn-close" onClick={onClose}>&times;</button>
          </div>
          <div className="modal-body">
            <div className="loading">Loading download status...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content download-status-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Download System Status</h2>
          <button className="btn-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="alert alert-error">
              {error}
            </div>
          )}

          {/* System Health */}
          <div className={`system-health ${getSystemHealthStatus()}`}>
            <h3>System Health</h3>
            <div className="health-message">{getSystemHealthMessage()}</div>

            {stats && (
              <div className="health-details">
                <div className="health-item">
                  <span className="label">yt-dlp Status:</span>
                  <span className={`value ${stats.download_system?.ytdlp_available ? 'available' : 'unavailable'}`}>
                    {stats.download_system?.ytdlp_available ? '✓ Available' : '✗ Not Installed'}
                  </span>
                </div>
                <div className="health-item">
                  <span className="label">Active Downloads:</span>
                  <span className="value">{stats.download_system?.active_downloads || 0}</span>
                </div>
              </div>
            )}
          </div>

          {/* Statistics */}
          {stats && (
            <div className="download-stats">
              <h3>Statistics</h3>
              <div className="stats-grid">
                <div className="stat-item">
                  <div className="stat-value">{stats.total_songs}</div>
                  <div className="stat-label">Total Songs</div>
                </div>
                <div className="stat-item success">
                  <div className="stat-value">{stats.completed_downloads}</div>
                  <div className="stat-label">Completed</div>
                </div>
                <div className="stat-item warning">
                  <div className="stat-value">{stats.pending_downloads}</div>
                  <div className="stat-label">Pending</div>
                </div>
                <div className="stat-item info">
                  <div className="stat-value">{stats.downloading_count}</div>
                  <div className="stat-label">Downloading</div>
                </div>
                <div className="stat-item danger">
                  <div className="stat-value">{stats.failed_downloads}</div>
                  <div className="stat-label">Failed</div>
                </div>
              </div>
            </div>
          )}

          {/* Active Downloads */}
          {activeDownloads.length > 0 && (
            <div className="active-downloads">
              <h3>Active Downloads ({activeDownloads.length})</h3>
              <div className="downloads-list">
                {activeDownloads.map(({ song, download_log }) => (
                  <div key={song.id} className="download-item active">
                    <div className="download-info">
                      <div className="song-title">{song.title}</div>
                      <div className="song-artist">{song.artist}</div>
                      <div className="download-progress">
                        Status: <span className="status-badge downloading">{download_log.status}</span>
                      </div>
                    </div>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleViewLog(song.id)}
                    >
                      View Log
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Failed Downloads */}
          {failedDownloads.length > 0 && (
            <div className="failed-downloads">
              <h3>Failed Downloads ({failedDownloads.length})</h3>
              <div className="downloads-list">
                {failedDownloads.map(({ song, download_log, error_message }) => (
                  <div key={song.id} className="download-item failed">
                    <div className="download-info">
                      <div className="song-title">{song.title}</div>
                      <div className="song-artist">{song.artist}</div>
                      <div className="error-message">{error_message}</div>
                    </div>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleViewLog(song.id)}
                    >
                      View Log
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No Downloads */}
          {activeDownloads.length === 0 && failedDownloads.length === 0 && (
            <div className="no-downloads">
              <p>No active or failed downloads</p>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-primary" onClick={fetchDownloadStatus}>
            Refresh Status
          </button>
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      {/* Detailed Log Modal */}
      {selectedSongLog && (
        <div className="modal-overlay" onClick={() => setSelectedSongLog(null)}>
          <div className="modal-content log-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Download Log - {selectedSongLog.song.title}</h2>
              <button className="btn-close" onClick={() => setSelectedSongLog(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="log-details">
                <div className="log-section">
                  <h3>Basic Information</h3>
                  <pre>{JSON.stringify({
                    song_id: selectedSongLog.song.id,
                    title: selectedSongLog.song.title,
                    youtube_url: selectedSongLog.song.youtube_url,
                    status: selectedSongLog.song.download_status,
                    ytdlp_available: selectedSongLog.ytdlp_available
                  }, null, 2)}</pre>
                </div>

                {selectedSongLog.download_log && Object.keys(selectedSongLog.download_log).length > 0 && (
                  <div className="log-section">
                    <h3>Download Log</h3>
                    <pre>{JSON.stringify(selectedSongLog.download_log, null, 2)}</pre>
                  </div>
                )}

                {selectedSongLog.song.error_message && (
                  <div className="log-section">
                    <h3>Error Message</h3>
                    <div className="error-text">{selectedSongLog.song.error_message}</div>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setSelectedSongLog(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DownloadStatus;